/* eslint-disable no-restricted-properties */
/* eslint-disable no-bitwise */

export interface TopNavInternalVariablesProps {
    topNavBackground: string;
    topNavColor: string;
    topNavActionItemBackground: string;
    topNavPlaceholderColor: string;
    topNavBorderColor: string;
    topNavBorderFocusColor: string;
    topNavTsLogoFilter: string;
}

export interface RGBColor {
    r: number;
    g: number;
    b: number;
}

export const isHexColor = (color: string): boolean => {
    return /^#[0-9A-Fa-f]{6}$/.test(color);
};

export const colorToHex = (color: string): string | null => {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return '#000000';
    ctx.fillStyle = color;
    return ctx.fillStyle.toLowerCase();
};

// Convert hex color to RGBColor
export const hexToRgb = (color: string): RGBColor => {
    const hex = isHexColor(color) ? color : colorToHex(color);
    const normalized = hex.replace(/^#/, '');
    const bigint = parseInt(normalized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
};

// Convert RGBColor to hex
export const rgbToHex = ({ r, g, b }: RGBColor): string => {
    return `#${[r, g, b]
        .map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? `0${hex}` : hex;
        })
        .join('')}`;
};

// sRGB to linear
export const linearize = (channel: number): number => {
    const c = channel / 255;
    return c < 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

// Luminance calculation
export const luminance = (color: string): number => {
    const { r, g, b } = hexToRgb(color);
    const rLin = linearize(r);
    const gLin = linearize(g);
    const bLin = linearize(b);
    return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
};

// Lighten color by a percentage
export const lighten = (color: string, percent: number): string => {
    const { r, g, b } = hexToRgb(color);
    const factor = percent / 100;
    const newR = Math.min(255, Math.round(r + (255 - r) * factor));
    const newG = Math.min(255, Math.round(g + (255 - g) * factor));
    const newB = Math.min(255, Math.round(b + (255 - b) * factor));
    return rgbToHex({ r: newR, g: newG, b: newB });
};

// Darken color by a percentage
export const darken = (color: string, percent: number): string => {
    const { r, g, b } = hexToRgb(color);
    const factor = percent / 100;

    const newR = Math.max(0, Math.round(r * (1 - factor)));
    const newG = Math.max(0, Math.round(g * (1 - factor)));
    const newB = Math.max(0, Math.round(b * (1 - factor)));

    return rgbToHex({ r: newR, g: newG, b: newB });
};

export const imageFilterLight = () => {
    return 'brightness(0) saturate(100%) invert(100%) sepia(20%) brightness(110%) contrast(90%)';
};

export const imageFilterDark = () => {
    return 'brightness(0) saturate(100%) invert(10%) sepia(10%) contrast(110%)';
};

// Lighten base color by a percentage
export const lightenBaseColor = (base: string, percent: number): string => {
    return lighten(base, percent);
};

// Darken base color by a percentage
export const darkenBaseColor = (base: string, percent: number): string => {
    return darken(base, percent);
};

export const prepareTopNavInternalVariables = (
    baseColor: string,
): TopNavInternalVariablesProps => {
    const WHITE = '#FFFFFF';
    const BLACK = '#000000';

    const baseColorLuminance = luminance(baseColor);
    const isBaseColorLight = baseColorLuminance >= 0.5;

    let topNavActionItemBackground;
    let topNavColor;
    let topNavBorderColor;
    let topNavBorderFocusColor;
    let topNavTsLogoFilter;
    if (isBaseColorLight) {
        topNavActionItemBackground = darkenBaseColor(baseColor, 7);
        topNavColor = lightenBaseColor(BLACK, 10);
        topNavBorderColor = lightenBaseColor(topNavActionItemBackground, 10);
        topNavBorderFocusColor = lightenBaseColor(
            topNavActionItemBackground,
            20,
        );
        topNavTsLogoFilter = imageFilterDark();
    } else {
        topNavActionItemBackground = lightenBaseColor(baseColor, 7);
        topNavColor = darkenBaseColor(WHITE, 10);
        topNavBorderColor = darkenBaseColor(topNavActionItemBackground, 10);
        topNavBorderFocusColor = darkenBaseColor(
            topNavActionItemBackground,
            20,
        );
        topNavTsLogoFilter = imageFilterLight();
    }

    let topNavPlaceholderColor;
    if (baseColorLuminance <= 0.2) {
        topNavPlaceholderColor = lightenBaseColor(baseColor, 40);
    } else if (baseColorLuminance <= 0.4) {
        topNavPlaceholderColor = lightenBaseColor(baseColor, 45);
    } else if (baseColorLuminance < 0.5) {
        topNavPlaceholderColor = lightenBaseColor(baseColor, 50);
    } else if (baseColorLuminance < 0.6) {
        topNavPlaceholderColor = darkenBaseColor(baseColor, 50);
    } else if (baseColorLuminance <= 0.8) {
        topNavPlaceholderColor = darkenBaseColor(baseColor, 45);
    } else {
        topNavPlaceholderColor = darkenBaseColor(baseColor, 40);
    }

    return {
        topNavBackground: baseColor,
        topNavColor,
        topNavActionItemBackground,
        topNavPlaceholderColor,
        topNavBorderColor,
        topNavBorderFocusColor,
        topNavTsLogoFilter,
    };
};
